import { QueryClient } from '@tanstack/react-query';

import { ApiError } from '@/shared/api/api-error';

/**
 * TanStack Query defaults.
 *
 * These are the conventions every feature inherits, so they are set once here
 * rather than repeated per-query (§7).
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * 30s. Admin data is operational and changes underneath the user, but
       * re-fetching on every remount makes tables flicker while someone is
       * working. 30 seconds keeps a tab switch cheap without going stale
       * enough to mislead. Features that need fresher data override this.
       */
      staleTime: 30_000,

      /** Keep unused data for 5 minutes so back-navigation is instant. */
      gcTime: 5 * 60_000,

      /**
       * Do not retry what cannot succeed. A 401/403/404 is a definitive answer
       * and retrying it three times just delays the error state by a second
       * and triples the load on a struggling server.
       */
      retry: (failureCount, error) => {
        if (error instanceof ApiError && !error.isRetryable) return false;
        return failureCount < 2;
      },

      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),

      /**
       * Off: an admin alt-tabbing to check something should not trigger a
       * storm of refetches across every open table. Explicit refresh and
       * mutation invalidation cover the real cases.
       */
      refetchOnWindowFocus: false,

      refetchOnReconnect: true,
    },
    mutations: {
      // Mutations are user-initiated and often non-idempotent — a silent retry
      // could create two brands. Failures surface to the user instead.
      retry: false,
    },
  },
});
